import { createContext, useContext, useState, type ReactNode } from 'react';
import { useAnalytics } from '@/services/analytics';
import { useMountEffect } from '@/hooks/use-mount-effect';

interface VideoLibraryContextValue {
  isOpen: boolean;
  activeVideoId: string | null;
  setActiveVideoId: (videoId: string | null) => void;
  openVideoLibrary: (videoId?: string) => void;
  closeVideoLibrary: () => void;
}

const VideoLibraryContext = createContext<VideoLibraryContextValue | null>(null);

interface VideoLibraryProviderProps {
  children: ReactNode;
}

export function VideoLibraryProvider({ children }: VideoLibraryProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const { track } = useAnalytics();

  const openVideoLibrary = (videoId?: string) => {
    setActiveVideoId(videoId ?? null);
    setIsOpen(true);
    void track('customer_io_basics', 'modal_viewed', {});
  };

  const closeVideoLibrary = () => {
    setIsOpen(false);
  };

  // Expose the video library functions to the window object
  // so that the ember ui can hook into it and open the video library
  useMountEffect(() => {
    Object.assign(window, {
      __hydra_video_library__: {
        open: openVideoLibrary,
        close: closeVideoLibrary,
      } satisfies Window['__hydra_video_library__'],
    });

    return () => {
      delete window.__hydra_video_library__;
    };
  });

  return (
    <VideoLibraryContext.Provider
      value={{ isOpen, activeVideoId, setActiveVideoId, openVideoLibrary, closeVideoLibrary }}
    >
      {children}
    </VideoLibraryContext.Provider>
  );
}

export function useVideoLibrary() {
  const context = useContext(VideoLibraryContext);

  if (context == null) {
    throw new Error('useVideoLibrary must be used within VideoLibraryProvider');
  }

  return context;
}
